import { useState } from 'react'
import { Alert, Box, Button, Checkbox, FormControlLabel, InputAdornment, MenuItem, Select, TextField, Typography } from '@mui/material'
import { Check } from '@mui/icons-material'
import { FormatCurrency } from 'core/Utilities'
import { FileBadge, FileNameLink } from './DocumentCard'
import { colors, outlinedPill, whiteInput } from './styles'
import {
  type DocumentPerson, type DocumentRequirement, type PendingDocument, type AddDocumentHandler,
  appliesToOptions, incomeYearOptions, newPendingDocument, pendingDocumentErrors
} from './types'

interface NewDocumentFormProps {
  requirement: DocumentRequirement
  child: DocumentPerson
  file: File
  onInclude: AddDocumentHandler
  onCancel: () => void
}

const questionSx = { fontSize: 14, color: colors.textPrimary }

export function NewDocumentForm (props: NewDocumentFormProps): JSX.Element {
  const [pending, setPending] = useState<PendingDocument>(() => newPendingDocument(props.requirement, props.file))
  const [submitted, setSubmitted] = useState(false)
  const [saving, setSaving] = useState(false)
  const [serverErrors, setServerErrors] = useState<string[]>([])

  const options = appliesToOptions(props.requirement, props.child)
  const validationErrors = pendingDocumentErrors(props.requirement, pending)
  const errors = [...(submitted ? validationErrors : []), ...serverErrors]

  const isSelected = (person: DocumentPerson): boolean =>
    person.isChild ? pending.includeChild : pending.caregiverIDs.includes(person.id)

  const toggle = (person: DocumentPerson): void => {
    if (person.isChild) {
      setPending({ ...pending, includeChild: !pending.includeChild })
    } else {
      const caregiverIDs = isSelected(person)
        ? pending.caregiverIDs.filter(id => id !== person.id)
        : [...pending.caregiverIDs, person.id]
      setPending({ ...pending, caregiverIDs })
    }
  }

  const changeAmount = (value: string): void => {
    const digits = value.replace(/[^0-9]/g, '')
    setPending({ ...pending, incomeAmount: digits === '' ? null : Number(digits) })
  }

  const include = async (): Promise<void> => {
    setSubmitted(true)
    setServerErrors([])
    if (validationErrors.length > 0) return
    setSaving(true)
    const result = await props.onInclude(pending)
    setSaving(false)
    setServerErrors(result)
  }

  return <Box data-testid='newDocumentForm' sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
      <FileBadge fileName={props.file.name} />
      <FileNameLink fileName={props.file.name} />
    </Box>

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', px: '20px', py: '12px', border: `1px solid ${colors.border}`, borderRadius: '10px', bgcolor: colors.cardBackground }}>
      {options.length > 0 && <>
        <Typography sx={questionSx}>Who does this document apply to?</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '10px' }}>
          {options.map(person =>
            <FormControlLabel
              key={`${person.isChild ? 'child' : 'caregiver'}-${person.id}`}
              label={person.name}
              sx={{ m: 0, gap: '10px', '& .MuiFormControlLabel-label': { fontSize: 12, color: colors.textPrimary } }}
              control={<Checkbox
                checked={isSelected(person)}
                onChange={() => { toggle(person) }}
                data-testid={`appliesTo-${person.isChild ? 'child' : person.id}`}
                sx={{ p: 0, color: colors.checkbox, '&.Mui-checked': { color: colors.checkbox } }}
              />}
            />
          )}
        </Box>
      </>}

      {props.requirement.incomeRelated && <>
        <Typography sx={questionSx}>What is the income defined in this document?</Typography>
        <Box sx={{ display: 'flex', gap: '10px' }}>
          <TextField
            size='small'
            placeholder='Amount'
            value={pending.incomeAmount === null ? '' : FormatCurrency(pending.incomeAmount, true)}
            onChange={e => { changeAmount(e.target.value) }}
            InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment>, sx: whiteInput }}
            inputProps={{ inputMode: 'numeric', 'aria-label': 'Income amount', 'data-testid': 'incomeAmount', style: { textAlign: 'right' } }}
            sx={{ width: 130 }}
          />
          <Select<number | ''>
            size='small'
            displayEmpty
            value={pending.incomeYear ?? ''}
            onChange={e => { setPending({ ...pending, incomeYear: e.target.value === '' ? null : Number(e.target.value) }) }}
            renderValue={value => value === '' ? <Box component='span' sx={{ color: 'text.disabled' }}>Year</Box> : value}
            SelectDisplayProps={{ 'aria-label': 'Income year' }}
            data-testid='incomeYear'
            sx={{ ...whiteInput, width: 100 }}
          >
            {incomeYearOptions(new Date().getFullYear()).map(year => <MenuItem key={year} value={year}>{year}</MenuItem>)}
          </Select>
        </Box>
      </>}

      {errors.length > 0 && <Alert severity='error' data-testid='newDocumentErrors' sx={{ borderRadius: '10px' }}>
        {errors.map(error => <div key={error}>{error}</div>)}
      </Alert>}

      <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'center', px: 2, py: 1.5 }}>
        <Button
          variant='outlined'
          onClick={props.onCancel}
          disabled={saving}
          data-testid='cancelDocument'
          sx={{ ...outlinedPill, borderRadius: '16px', height: 33, textTransform: 'none', boxShadow: '0px 8px 10px 0px rgba(113, 54, 188, 0.2)' }}
        >
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={() => { void include() }}
          disabled={saving}
          endIcon={<Check />}
          data-testid='includeDocument'
          sx={{ bgcolor: colors.purple, borderRadius: '16px', height: 33, px: '28px', fontSize: 14, fontWeight: 600, textTransform: 'none', boxShadow: '0px 8px 10px -4px rgba(113, 54, 188, 0.2)', '&:hover': { bgcolor: colors.purple } }}
        >
          Include
        </Button>
      </Box>
    </Box>
  </Box>
}
